import { createSlice } from "@reduxjs/toolkit";

import { reset } from "../detail/shreddingDetailSlice";
import { COMMENT_CARD_KEY, isColumnCardKey, toColumnId } from "./columnFilters";

export const INITIAL_STATE = {
  selectedTermIds: [],
  withComments: false,
  removedDefaultCards: [],
  addedCards: [],
  columnFilters: {},
};

const EMPTY_COLUMN_FILTER = { values: [], notSet: false };

const clearCard = (state, cardKey) => {
  if (cardKey === COMMENT_CARD_KEY) {
    state.withComments = false;
  } else if (isColumnCardKey(cardKey)) {
    delete state.columnFilters[toColumnId(cardKey)];
  }
};

const clearDictionary = (state) => {
  if (state.selectedTermIds.length) {
    state.selectedTermIds = [];
  }
};

const clearFilters = (state) => {
  state.columnFilters = {};
  state.withComments = false;
};

const keepOnlyColumnFilter = (state, columnId) => {
  const kept = state.columnFilters[columnId];
  clearFilters(state);
  if (kept) {
    state.columnFilters[columnId] = kept;
  }
};

const storeColumnFilter = (state, columnId, filter) => {
  if (filter.values.length || filter.notSet) {
    state.columnFilters[columnId] = filter;
  } else {
    delete state.columnFilters[columnId];
  }
};

const shreddingFiltersSlice = createSlice({
  name: "filters",
  initialState: INITIAL_STATE,
  reducers: {
    resetAll: () => INITIAL_STATE,
    setSelectedTermIds: (state, { payload = [] }) => {
      clearFilters(state);
      state.selectedTermIds = payload;
    },
    toggleWithComments: (state) => {
      const withComments = !state.withComments;
      clearDictionary(state);
      clearFilters(state);
      state.withComments = withComments;
    },
    toggleColumnFilterValue: (state, { payload: { columnId, value } }) => {
      clearDictionary(state);
      keepOnlyColumnFilter(state, columnId);
      const current = state.columnFilters[columnId] ?? EMPTY_COLUMN_FILTER;
      const values = current.values.includes(value)
        ? current.values.filter((ticked) => ticked !== value)
        : [...current.values, value];
      storeColumnFilter(state, columnId, { ...current, values });
    },
    toggleColumnFilterNotSet: (state, { payload: columnId }) => {
      clearDictionary(state);
      keepOnlyColumnFilter(state, columnId);
      const current = state.columnFilters[columnId] ?? EMPTY_COLUMN_FILTER;
      storeColumnFilter(state, columnId, {
        ...current,
        notSet: !current.notSet,
      });
    },
    removeFilterCard: (state, { payload: { key, isDefault } }) => {
      clearCard(state, key);
      if (isDefault) {
        state.removedDefaultCards = [
          ...new Set([...state.removedDefaultCards, key]),
        ];
      } else {
        state.addedCards = state.addedCards.filter((added) => added !== key);
      }
    },
    setShownFilterCards: (state, { payload: { keys, cards } }) => {
      const shown = new Set(keys);
      cards
        .filter(({ key }) => !shown.has(key))
        .forEach(({ key }) => clearCard(state, key));
      state.removedDefaultCards = cards
        .filter(({ key, isDefault }) => isDefault && !shown.has(key))
        .map(({ key }) => key);
      const nonDefaultShown = cards
        .filter(({ key, isDefault }) => !isDefault && shown.has(key))
        .map(({ key }) => key);
      state.addedCards = [
        ...state.addedCards.filter((key) => nonDefaultShown.includes(key)),
        ...nonDefaultShown.filter((key) => !state.addedCards.includes(key)),
      ];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reset, () => INITIAL_STATE);
  },
});

export const {
  resetAll,
  setSelectedTermIds,
  toggleWithComments,
  toggleColumnFilterValue,
  toggleColumnFilterNotSet,
  removeFilterCard,
  setShownFilterCards,
} = shreddingFiltersSlice.actions;

export default shreddingFiltersSlice;
